import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { LogOut } from "lucide-react";
import BRAND from "../brand";
import { StatsGrid } from "./dashboard/StatsGrid";
import { ScenarioList } from "./dashboard/ScenarioList";
import { Sidebar } from "./dashboard/Sidebar";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [scenariosRes, analyticsRes, sessionsRes] = await Promise.all([
          axios.get(`${API}/scenarios`),
          axios.get(`${API}/analytics/overview`),
          axios.get(`${API}/sessions`)
        ]);
        setScenarios(scenariosRes.data);
        setAnalytics(analyticsRes.data);
        setRecentSessions(sessionsRes.data.slice(0, 3));
      } catch (e) {
        console.error("[Dashboard] Failed to load data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-[#2563EB] rounded-full animate-spin"></div>
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-[#0B1F3B]">{BRAND.productName}</h1>
                <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm font-medium text-[#0B1F3B]">Dashboard</Link>
              <Link to="/simulator" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Practice</Link>
              <Link to="/history" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">History</Link>
              {(user?.role === "admin" || user?.role === "sales_head" || user?.role === "manager") && (
                <Link to="/analytics" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Team</Link>
              )}
              {user?.role === "admin" && (
                <Link to="/admin" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Admin</Link>
              )}
            </nav>
            <div className="flex items-center gap-4">
              <Link to="/profile" className="text-right hidden sm:block hover:opacity-80 transition-opacity">
                <p className="text-sm font-medium text-[#0B1F3B]">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="text-slate-500 hover:text-[#0B1F3B]">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 fade-up">
          <h2 className="text-3xl font-bold text-[#0B1F3B] mb-2">Welcome back, {user?.name?.split(' ')[0]}</h2>
          <p className="text-slate-500">{BRAND.positioning.subheadline}</p>
        </div>

        <StatsGrid analytics={analytics} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ScenarioList scenarios={scenarios} />
          </div>
          <Sidebar analytics={analytics} recentSessions={recentSessions} />
        </div>
      </main>

      <footer className="border-t border-slate-200/60 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-6 w-auto opacity-60" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-sm text-slate-400">{BRAND.productName}</span>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
              <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
